import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Plus, Trash2, FolderGit2, ChevronDown, ChevronUp } from "lucide-react";
import { ImageUpload } from "@/components/ui/ImageUpload";
import {
  draftProjectsContentSchema,
  type DraftProjectsContent,
  type DraftProjectItem,
  type ProjectRole,
} from "@profolio/validators";

interface ProjectsEditorProps {
  content: Record<string, unknown>;
  onChange: (content: DraftProjectsContent) => void;
}

export function ProjectsEditor({ content, onChange }: ProjectsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);

  const parseResult = draftProjectsContentSchema.safeParse(content);
  const data: DraftProjectsContent = parseResult.success
    ? parseResult.data
    : { heading: "Projects & Grants", displayStyle: "cards", items: [] };

  const heading = data.heading || "Projects & Grants";
  const displayStyle = data.displayStyle || "cards";
  const items: DraftProjectItem[] = data.items || [];

  const notifyChange = (
    newHeading: string,
    newStyle: "cards" | "list",
    newItems: DraftProjectItem[]
  ) => {
    onChange({
      heading: newHeading,
      displayStyle: newStyle,
      items: newItems,
    });
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    notifyChange(e.target.value, displayStyle, items);
  };

  const handleStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as "cards" | "list";
    notifyChange(heading, val, items);
  };

  const addItem = () => {
    if (items.length >= 50) return;
    const newId = crypto.randomUUID();
    const newItem: DraftProjectItem = {
      id: newId,
      title: "",
      role: "pi",
      fundingAgency: "",
      grantNumber: "",
      amount: "",
      startDate: "",
      endDate: "",
      isCurrent: true,
      collaborators: "",
      description: "",
      projectUrl: "",
      imageUrl: "",
      tags: [],
    };
    const updated = [newItem, ...items];
    setExpandedIndex(0);
    setNewlyAddedId(newId);
    notifyChange(heading, displayStyle, updated);
  };

  const updateItem = (index: number, patch: Partial<DraftProjectItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...patch };
    notifyChange(heading, displayStyle, updated);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    if (expandedIndex === index) setExpandedIndex(null);
    notifyChange(heading, displayStyle, updated);
  };

  const handleTagsChange = (index: number, rawTags: string) => {
    const tags = rawTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    updateItem(index, { tags });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="projects-heading" className="text-xs font-semibold">
            Section Heading
          </Label>
          <Input
            id="projects-heading"
            value={heading}
            onChange={handleHeadingChange}
            placeholder="Projects & Grants"
            className="mt-1 text-xs"
          />
        </div>
        <div>
          <Label htmlFor="projects-style" className="text-xs font-semibold">
            Display Layout
          </Label>
          <select
            id="projects-style"
            value={displayStyle}
            onChange={handleStyleChange}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm"
          >
            <option value="cards" className="bg-card text-foreground">2-Column Responsive Cards</option>
            <option value="list" className="bg-card text-foreground">Single Column Detailed List</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Research Grants & Projects ({items.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Document your sponsored research grants, partnerships, and leadership roles.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={addItem}
            disabled={items.length >= 50}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Grant / Project
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={FolderGit2}
            title="No projects or grants added yet"
            description="Showcase your funded research, PI leadership roles, and sponsor awards."
            actionLabel="Add First Project"
            onAction={addItem}
          />
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <div
                  key={item.id || index}
                  className="rounded-lg border bg-card p-3.5 transition-all shadow-2xs hover:border-primary/30"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className="flex-1 cursor-pointer font-medium text-xs flex items-center gap-2 min-w-0"
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    >
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                        {item.role === "pi" ? "PI" : item.role.toUpperCase()}
                      </span>
                      <span className="truncate text-foreground font-semibold">
                        {item.title || "Untitled Grant / Project"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className="h-7 w-7 text-muted-foreground"
                      >
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3.5 space-y-3 pt-3.5 border-t">
                      {/* 1. Essential Fields */}
                      <div>
                        <Label className="text-xs font-medium">Project / Grant Title *</Label>
                        <Input
                          placeholder="e.g. Next-Generation Climate Modeling with Edge AI"
                          value={item.title}
                          autoFocus={newlyAddedId === item.id}
                          onFocus={() => setNewlyAddedId(null)}
                          onChange={(e) => updateItem(index, { title: e.target.value })}
                          className="mt-1 text-xs"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Your Role</Label>
                          <select
                            value={item.role}
                            onChange={(e) => updateItem(index, { role: e.target.value as ProjectRole })}
                            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm"
                          >
                            <option value="pi" className="bg-card text-foreground">Principal Investigator (PI)</option>
                            <option value="co_pi" className="bg-card text-foreground">Co-Principal Investigator</option>
                            <option value="lead_researcher" className="bg-card text-foreground">Lead Researcher</option>
                            <option value="postdoc" className="bg-card text-foreground">Postdoctoral Scholar</option>
                            <option value="collaborator" className="bg-card text-foreground">Collaborator / Co-I</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Funding Agency / Sponsor</Label>
                          <Input
                            placeholder="e.g. National Science Foundation (NSF)"
                            value={item.fundingAgency || ""}
                            onChange={(e) => updateItem(index, { fundingAgency: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Grant Amount / Value</Label>
                          <Input
                            placeholder="e.g. $450,000 USD or Rs. 5,000,000"
                            value={item.amount || ""}
                            onChange={(e) => updateItem(index, { amount: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      {/* 2. Progressive Disclosure: Dates, Tags, URLs & Description */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Start Date / Year</Label>
                          <Input
                            placeholder="e.g. 2024 or 2024-01"
                            value={item.startDate || ""}
                            onChange={(e) => updateItem(index, { startDate: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">End Date</Label>
                          <Input
                            placeholder="e.g. 2027"
                            disabled={item.isCurrent}
                            value={item.endDate || ""}
                            onChange={(e) => updateItem(index, { endDate: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                          <input
                            type="checkbox"
                            id={`current-${index}`}
                            checked={item.isCurrent}
                            onChange={(e) => updateItem(index, { isCurrent: e.target.checked })}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <Label htmlFor={`current-${index}`} className="text-xs cursor-pointer font-medium">
                            Active / Ongoing Project
                          </Label>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Grant Reference Number</Label>
                          <Input
                            placeholder="e.g. NSF-2024-8842"
                            value={item.grantNumber || ""}
                            onChange={(e) => updateItem(index, { grantNumber: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Project Website / Repo Link</Label>
                          <Input
                            placeholder="https://climate-edge.org"
                            value={item.projectUrl || ""}
                            onChange={(e) => updateItem(index, { projectUrl: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Keyword Tags (comma separated)</Label>
                        <Input
                          placeholder="e.g. Edge Computing, Climate AI, Sensor Networks"
                          value={item.tags ? item.tags.join(", ") : ""}
                          onChange={(e) => handleTagsChange(index, e.target.value)}
                          className="mt-1 text-xs"
                        />
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Abstract & Project Goals</Label>
                        <Textarea
                          placeholder="Summary of research methodologies, partners, and expected impact..."
                          value={item.description || ""}
                          onChange={(e) => updateItem(index, { description: e.target.value })}
                          rows={3}
                          className="mt-1 text-xs"
                        />
                      </div>

                      <div className="pt-2 border-t border-border/40">
                        <Label className="text-xs font-medium">Project Cover Figure / Diagram</Label>
                        <p className="text-[11px] text-muted-foreground mb-2">
                          Upload a project architecture diagram, research poster, or cover graphic.
                        </p>
                        <ImageUpload
                          value={item.imageUrl || null}
                          onChange={(url) => updateItem(index, { imageUrl: url })}
                          category="figure"
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
